import {
  AfterViewInit,
  Component,
  computed,
  effect,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  runInInjectionContext,
  ViewChild,
} from '@angular/core';
import { AuthService } from './services/auth-service/auth.service';
import { LoggerService } from './services/logger-service/logger.service';
import { environment } from '../environments/environment';
import { MatDrawer } from '@angular/material/sidenav';
import { DrawerService } from './shared/mat-drawer/drawer.service';
import { IconUtil } from './utils/icon-util/icon-util';
import { MaterialRoundedIcons } from './utils/icon-util/material-icons';
import { MatIconRegistry } from '@angular/material/icon';
import { MaterialIconCssClass } from './utils/icon-util/material-icon-css-class';
import { DomSanitizer } from '@angular/platform-browser';
import { LocalAssetIcons } from './utils/icon-util/local-icons';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('drawer', { static: false }) drawer: MatDrawer;

  private readonly injector = inject(Injector);
  private readonly authService = inject(AuthService);
  private readonly loggerService = inject(LoggerService);
  private readonly drawerService = inject(DrawerService);
  private readonly matIconRegistry = inject(MatIconRegistry);
  private readonly domSanitizer = inject(DomSanitizer);

  constructor() {}

  drawerConfig = computed(() => this.drawerService.drawerConfig());

  ngOnInit() {
    this.authService.onInit();
    this.renderAppDynamicsScripts();
    this.drawerService.initDrawerSetup();
    this.handleDrawerActions();
    this.handleMaterialIcons();
    this.handleAssetIcons();
  }

  ngOnDestroy(): void {
    // Flush any pending logs to ensure that any remaining queued messages are sent to Sumo Logic.
    this.loggerService.flushLogs();
  }

  ngAfterViewInit(): void {
    this.drawer.closedStart.subscribe(() => {
      this.drawerService.close();
    });
  }

  closeDrawer() {
    this.drawerService.close(); // Update the service state first!
    this.drawer.close();
  }

  private handleMaterialIcons() {
    // Register rounded material icons
    this.matIconRegistry.setDefaultFontSetClass(MaterialIconCssClass.Rounded);
    IconUtil.ensureMaterialIcon24px400w('Rounded', MaterialRoundedIcons);
  }

  private handleAssetIcons() {
    // Register local SVG asset icons
    IconUtil.registerLocalSvgIcons(
      this.matIconRegistry,
      this.domSanitizer,
      LocalAssetIcons,
      'local' // namespace
    );
  }

  // Isolating drawer actions to a separate injection context
  // to avoid triggering unwanted subscriptions.
  private handleDrawerActions() {
    runInInjectionContext(this.injector, () => {
      effect(() => {
        if (this.drawerService.openDrawer()) {
          this.drawer.open();
        } else {
          this.drawer?.close();
        }
      });
    });
  }

  private renderAppDynamicsScripts() {
    const appKey = environment?.appDynamicsKey;

    if (appKey) {
      const js = `
        window["adrum-start-time"] = new Date().getTime();
        (function(config){
            config.appKey = "${appKey}";
            config.adrumExtUrlHttp = "http://cdn.appdynamics.com";
            config.adrumExtUrlHttps = "https://cdn.appdynamics.com";
            config.beaconUrlHttp = "http://pdx-col.eum-appdynamics.com";
            config.beaconUrlHttps = "https://pdx-col.eum-appdynamics.com";
            config.useHTTPSAlways = true;
            config.resTiming = {"bufSize":200,"clearResTimingOnBeaconSend":true};
            config.maxUrlLength = 512;
            config.isZonePromise = true;
            config.angular = true;
        })(window["adrum-config"] || (window["adrum-config"] = {}));
      `;
      const scriptJs = document.createElement('script');
      scriptJs.type = 'text/javascript';
      scriptJs.innerHTML = js;
      const body = document.getElementsByTagName('body')[0];

      const scriptAppDJsLibCdn = document.createElement('script');
      scriptAppDJsLibCdn.src = '//cdn.appdynamics.com/adrum/adrum-23.10.1.4359.js';

      body.appendChild(scriptJs);
      body.appendChild(scriptAppDJsLibCdn);
    }
  }
}
